import { useState, useEffect } from 'react';

import { AlertTriangle, Check, Clock, Loader2, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { cacheTtlApi } from '@/lib/api/supabase/cache-ttl.api';

const MIN_TTL = 1;
const MAX_TTL = 86400; // 24 hours

interface CacheTtlHITLProps {
  projectId: string;
  endpointPath: string;
  requestedTtl?: number;
  isClicked: boolean;
  isCancelled?: boolean;
  isStale?: boolean;
  onSubmitted?: (path: string, ttl: number) => void;
  onCancelled?: () => void;
}

type Phase = 'loading' | 'ready' | 'error';

/** Human-readable form, e.g. 300 -> "5 minutes". */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'}`;
  if (seconds < 3600) {
    const m = seconds / 60;
    return `${Number.isInteger(m) ? m : m.toFixed(1)} minute${m === 1 ? '' : 's'}`;
  }
  const h = seconds / 3600;
  return `${Number.isInteger(h) ? h : h.toFixed(1)} hour${h === 1 ? '' : 's'}`;
}

/** Last path segment, e.g. "/functions/v1/get-announcements" -> "get-announcements". */
function endpointName(path: string): string {
  return path.split('/').filter(Boolean).pop() || path;
}

/**
 * CacheTtlHITL - inline card to set a per-endpoint Cache TTL override.
 *
 * Flow:
 * 1. Fetches the current effective TTL for the endpoint (loading phase)
 * 2. Shows a number input prefilled with the requested-or-current value
 * 3. Direction-aware warning (raise = staleness, lower = cost)
 * 4. On submit: POSTs the override to the backend, which signs + forwards
 *    to the gateway admin endpoint
 */
export function CacheTtlHITL({
  projectId,
  endpointPath,
  requestedTtl,
  isClicked,
  isCancelled,
  isStale,
  onSubmitted,
  onCancelled,
}: CacheTtlHITLProps) {
  const { getIdToken } = useAuth();

  const [phase, setPhase] = useState<Phase>('loading');
  const [currentTtl, setCurrentTtl] = useState<number>(0);
  const [defaultTtl, setDefaultTtl] = useState<number>(0);
  const [hasOverride, setHasOverride] = useState(false);
  const [ttlValue, setTtlValue] = useState<number>(requestedTtl ?? 0);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch current effective TTL on mount — but ONLY for an active card.
  // Terminal cards (submitted / cancelled / superseded) render a static badge
  // and don't need the value; skipping them avoids a burst of redundant
  // /admin/ttl-override GETs when a long chat history re-mounts on refresh.
  useEffect(() => {
    if (isClicked || isCancelled || isStale) {
      return;
    }
    let cancelled = false;

    const load = async () => {
      try {
        const token = await getIdToken();
        if (!token || cancelled) return;

        const status = await cacheTtlApi.getStatus(token, projectId, endpointPath);
        if (cancelled) return;

        setCurrentTtl(status.effectiveTtl);
        setDefaultTtl(status.defaultTtl);
        setHasOverride(status.override !== null);
        setTtlValue(requestedTtl ?? status.effectiveTtl);
        setPhase('ready');
      } catch (err: unknown) {
        if (cancelled) return;
        const axiosErr = err as { response?: { data?: { message?: string } } };
        setErrorMessage(
          axiosErr.response?.data?.message ||
            (err instanceof Error ? err.message : 'Failed to load cache TTL'),
        );
        setPhase('error');
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [projectId, endpointPath, requestedTtl, getIdToken, isClicked, isCancelled, isStale]);

  const handleSubmit = async () => {
    if (ttlValue < MIN_TTL || ttlValue > MAX_TTL || ttlValue === currentTtl) return;
    setIsSubmitting(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Authentication required');
      await cacheTtlApi.setOverride(token, projectId, endpointPath, ttlValue);
      onSubmitted?.(endpointPath, ttlValue);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setErrorMessage(
        axiosErr.response?.data?.message ||
          (err instanceof Error ? err.message : 'Failed to set cache TTL'),
      );
      setIsSubmitting(false);
    }
  };

  // -- Completed state (persisted across reloads via clickedActions) --
  if (isClicked) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 w-fit">
        <div className="flex items-center gap-2 text-sm">
          <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-green-500">
            <Check className="h-3 w-3 text-white" strokeWidth={3} />
          </span>
          <span className="font-medium text-foreground">Cache TTL updated</span>
        </div>
      </div>
    );
  }

  // -- Cancelled state --
  if (isCancelled) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 w-fit">
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <XCircle className="w-3.5 h-3.5 text-muted-foreground" />
          <span>Cancelled</span>
        </div>
      </div>
    );
  }

  // -- Stale state (user sent a new message after this HITL) --
  if (isStale) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 w-fit">
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <span>Skipped</span>
        </div>
      </div>
    );
  }

  // -- Loading state --
  if (phase === 'loading') {
    return (
      <div className="bg-card border border-border rounded-lg p-5 w-full max-w-md">
        <div className="flex items-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Loading cache settings...</span>
        </div>
      </div>
    );
  }

  // -- Error state --
  if (phase === 'error') {
    return (
      <div className="bg-card border border-destructive/30 rounded-lg p-4 w-full max-w-md space-y-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-destructive" />
          <span className="text-sm font-medium">Couldn&apos;t load cache settings</span>
        </div>
        <p className="text-xs text-muted-foreground">{errorMessage || 'Unknown error'}</p>
      </div>
    );
  }

  // -- Ready: number input + actions --
  const invalid = ttlValue < MIN_TTL || ttlValue > MAX_TTL || !Number.isFinite(ttlValue);
  const noChange = ttlValue === currentTtl;

  return (
    <div className="bg-card border border-border rounded-lg w-full max-w-md overflow-hidden">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
            <Clock className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-medium truncate">
              Cache TTL for <span className="font-mono">{endpointName(endpointPath)}</span>
            </h4>
            <p className="text-xs text-muted-foreground">
              Current: {formatDuration(currentTtl)}
              {hasOverride ? ' (custom)' : ' (gateway default)'}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Number input */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-foreground" htmlFor="cache-ttl-input">
            New cache TTL (seconds)
          </label>
          <input
            id="cache-ttl-input"
            type="number"
            min={MIN_TTL}
            max={MAX_TTL}
            value={Number.isFinite(ttlValue) ? ttlValue : ''}
            onChange={(e) => setTtlValue(Math.floor(Number(e.target.value)))}
            disabled={isSubmitting}
            className={cn(
              'w-full px-3 py-2 rounded-md border bg-background text-sm',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              invalid ? 'border-destructive' : 'border-border',
            )}
          />
          <p className="text-[10px] text-muted-foreground">
            {invalid
              ? `Enter a value between ${MIN_TTL} and ${MAX_TTL} seconds (24h max).`
              : `= ${formatDuration(ttlValue)}. Higher caches are less costly but less fresh; lower caches are more fresh but more costly.`}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <Button
            variant="default"
            size="sm"
            onClick={handleSubmit}
            disabled={isSubmitting || invalid || noChange}
            className="flex-1 h-8 px-3 text-xs"
          >
            {isSubmitting && <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />}
            {noChange ? 'No change' : 'Set TTL'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCancelled?.()}
            disabled={isSubmitting}
            className="h-8 px-3 text-xs"
          >
            Cancel
          </Button>
        </div>

        {/* Error banner */}
        {errorMessage && <p className="text-xs text-destructive">{errorMessage}</p>}
      </div>
    </div>
  );
}
